import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import AppBar from "../../../common/AppBar";
import SubmitRevisionModal from "./Modals/SubmitRevisionModal";
import RejectRevisionModal from "./Modals/RejectRevisionModal";
import ApproveRevisionModal from "./Modals/ApproveRevisionModal";
import RequestChangeModal from "./Modals/RequestChangeModal";
import ReleaseRevisionModal from "./Modals/ReleaseRevisionModal";
import Snackbar from "../../../common/Snackbar";
import { BASE_URL } from "../../../config";
import { getAuditTrailData } from "../../../util/getAuditTrailData";
import {
  getAllLockStatus,
  lockTask,
  unlockTask,
  getRevisionLockStatus,
} from "../../../util/apiService";
import { getProcedureRevisionsForPart } from "../../../redux/procedureRevisionSlice";
import { openPreview, openPreviewWithCache } from "../../../redux/previewSlice";
import { buildPermissionContext, PERMISSIONS } from "../../../util/permissions";
import { setTaskReference } from "../../Tasks/Uitlities";
import OpenTasksPopup from "../../Tasks/Popups/OpenTasksPopup";
import { formatDateToAppTimezoneAlt } from "../../../util/timezoneUtils";
import {
  buildInheritedFullyTrainedTraineeIds,
  findLatestReleasedProcedureRevision,
  resolveRevisionRevLabelFromContext,
  fetchTrainingStatusLabelForProcedureRevisionForTrainee,
  isRevisionWorkflowActor,
} from "../../../util/trainingLogStatus";
import PrimaryButton from "../../../common/Buttons/PrimaryButton";
import OutlineButton from "../../../common/Buttons/OutlineButton";
import { setGlobalAppViewMode } from "../../../util/appViewModeStorage";

/**
 * DocumentAppBar - Self-contained AppBar for document revision pages
 * Handles common document operations with internal logic and modals
 */
const DocumentAppBar = ({
  // Document Info
  type,
  partNumber,
  revision,
  revisionId,
  partId,
  description,

  // Revision Data
  revisionData = [],
  trainingLogs = [],
  /** Parent bumps after training log save so we refetch GET /training-log/get-all for the header */
  trainingStatusRefreshKey = 0,
  bomTable = [],
  currentRevision = null,
  currentStatus: currentStatusProp = null,
  allNodesData = [],

  // Lock Status
  lockStatus = null,

  // Optional Handlers (for version history from parent)
  onOpenVersionHistory,

  // User Info
  userName = localStorage.getItem("user_name"),

  // AppBar Options
  showTablesDropdown = true,
  showBlackTitle = true,
  showBookmark = true,
  /** Parent toggles view vs live mode (e.g. Document bookmark). Must be forwarded to `AppBar`. */
  onBookmarkClick = null,
  navigateTwoPagesBack = true,
  
  /** ISO time from audit trail row (version_created_at / audit_created_at / audit_date) for "Saved Version on …" subtitle */
  auditTrailSavedAtIso = null,

  // Custom Buttons (override defaults)
  customButtons = null,
  customButtonsBeforeTitle = null,

  // Optional custom handlers (for advanced use cases)
  onBack = null,

  // Optional callbacks
  onSubmitSuccess = null,
  onRejectSuccess = null,
  onCloseAdditionalInfo = null,
  onGetTrainingClick = null,
  /** Live (view) mode: optional; defaults to same flow as Get Training when omitted. */
  onGiveTrainingClick = null,
  /** Optional; default shows an info snackbar. Live (view) mode only — "?" next to Get Training. */
  onTrainingHelpClick = null,

  // Disable state for tab coordination
  disabled = false,
  previewDisabled = false,
  previewDisabledTitle = "",
  showProfileButton=true,
  showBackButton=true,
  usingInMagicLink=false,
  isViewMode = false,
  /** After "Select the Trainer" → Next: simplified app bar (view / live mode only). */
  trainingCheckmarkMode = false,
  trainerDisplayName = "",
  traineeDisplayName = "",
  onTrainingCheckmarkCancel = () => {},
  onTrainingCheckmarkNext = () => {},

  fromAuditTrail = false,
  cancel_screen_data = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const appSetupState = useSelector((state) => state.appSetup);
  const permissionCtx = buildPermissionContext(authUser);
  const canUserViewStrict = permissionCtx.permissions?.View === true;
  const canApprove = permissionCtx.permissions?.[PERMISSIONS.APPROVE] === true;
  const canRelease = permissionCtx.permissions?.[PERMISSIONS.RELEASE] === true;
  const canArchive = permissionCtx.permissions?.[PERMISSIONS.ARCHIVE] === true;
  const canReject = permissionCtx.permissions?.[PERMISSIONS.REJECT] === true;

  // Internal modal states
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRequestChangeModalOpen, setIsRequestChangeModalOpen] =
    useState(false);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);

  /** Live mode app bar: label from GET /training-log/get-all?procedure_revision_ref=… */
  const [viewerTrainingStatusLabel, setViewerTrainingStatusLabel] =
    useState(null);

  const [openTasksState, setOpenTasksState] = useState({
    isOpen: false,
    ltree_path: null,
    ltree_path: null,
    operation: null,
    title: null,
  });

  const proceedWithOperation = (operation) => {
    switch (operation) {
      case "submit":
        setIsApprovalModalOpen(true);
        break;
      case "reject":
        setIsRejectModalOpen(true);
        break;
      case "approve":
        setIsApproveModalOpen(true);
        break;
      case "request_change":
        setIsRequestChangeModalOpen(true);
        break;
      case "release":
        setIsReleaseModalOpen(true);
        break;
      case "archive":
        performArchive();
        break;
      default:
        break;
    }
  };

  const handleTaskCheck = (operation) => {
    // Close additional information container if open
    if (onCloseAdditionalInfo) {
      onCloseAdditionalInfo();
    }
    const ltree_path = currentRevision?.tree_node_details?.ltree_path;

    if (ltree_path) {
      const popupTitle = `Pending Tasks for ${partNumber}, Rev ${revision}`;
      setOpenTasksState({
        isOpen: true,
        ltree_path,
        operation,
        title: popupTitle,
      });
    } else {
      proceedWithOperation(operation);
    }
  };

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  /**
   * Latest Live/Edit mode — `handleBackClick` awaits unlock; use ref so Back uses post-toggle mode.
   * Sync in render (not only in useEffect): after Live→Edit toggle, effect runs after paint; Back can
   * run first and would otherwise pass stale Live mode to `/new-module` (seen when state is tight).
   */
  const isViewModeRef = useRef(isViewMode);
  isViewModeRef.current = isViewMode;

  // ============================================================
  // INTERNAL HANDLERS
  // ============================================================

  /**
   * Handle back button click
   * Navigates back to AddModule if we came from there, otherwise uses default back navigation
   * Also unlocks the revision when user navigates away
   */
  const handleBackClick = async () => {

    // Unlock the revision when user navigates away
    // Simplified condition: if user and revisionId exist, try to unlock
    // The lock was created by lockTask, so we should attempt to unlock it
    if (authUser && revisionId) {
      try {
        const unlockData = {
          task_id: `revision_${revisionId}`,
          user_id: authUser.id,
          user_name:
            `${authUser.first_name || ""} ${authUser.last_name || ""}`.trim() ||
            authUser.email ||
            "Unknown User",
          action: "edit",
        };

        await unlockTask(unlockData);
      } catch (error) {
        console.error(
          "❌ Failed to unlock document on back navigation:",
          error,
        );
      }
    } else {
    }

    // Check both location.state and sessionStorage for navigation origin
    let navOrigin = null;

    // First check location.state (for direct navigation)
    if (location.state?.fromAddModule) {
      navOrigin = {
        fromAddModule: true,
        moduleData: location.state.moduleData || null,
        isUnlink: location.state.isUnlink || false,
        /** Stale if we only used state / sessionStorage — use current Document Live vs Edit (prop). */
        isViewMode,
      };
    } else {
      // Check sessionStorage (for preserved navigation through intermediate screens)
      const storageKey = `navFromAddModule_${partId}_${revisionId}`;
      try {
        const storedNav = sessionStorage.getItem(storageKey);
        if (storedNav) {
          navOrigin = JSON.parse(storedNav);
        }
      } catch (error) {
        console.error(
          "Error reading navigation state from sessionStorage:",
          error,
        );
      }
    }

    // If we came from AddModule, navigate back with preserved state
    if (navOrigin?.fromAddModule) {
      const modeNow = isViewModeRef.current;
      // Clean up sessionStorage
      const storageKey = `navFromAddModule_${partId}_${revisionId}`;
      try {
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.error(
          "Error removing navigation state from sessionStorage:",
          error,
        );
      }

      // Align Add Module session with the mode shown on Document (incl. after Live ↔ Edit toggle).
      try {
        setGlobalAppViewMode(modeNow);
        if (revisionId) {
          sessionStorage.setItem(
            `document_is_view_mode_${revisionId}`,
            String(modeNow),
          );
        }
        const mid =
          navOrigin.moduleData?.module_id ??
          location.state?.moduleData?.module_id;
        if (mid != null && String(mid).trim() !== "") {
          sessionStorage.setItem(
            `addModule_isViewMode_${mid}`,
            String(modeNow),
          );
        }
        /** Survives when production deploy drops `location.state` on `/new-module` navigation. */
        sessionStorage.setItem(
          "addModule_pending_doc_view_mode",
          String(modeNow),
        );
      } catch {
        /* ignore */
      }

      // Navigate back to AddModule with the preserved state.
      // Always send the **current** Document view mode so Live/Edit toggle before Back is respected
      // (sessionStorage still holds the mode from when the revision was opened).
      navigate("/new-module", {
        state: {
          moduleData: navOrigin.moduleData || null,
          isUnlink: navOrigin.isUnlink || false,
          isViewMode: modeNow,
          fromDocumentBack: true,
        },
      });
    } else if (fromAuditTrail) {
      const auditTrailBackId =
        location.state?.auditTrailBackId ||
        (typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem("auditTrailBackAuditTrailId")
          : null);
      if (auditTrailBackId) {
        try {
          sessionStorage.removeItem("auditTrailBackAuditTrailId");
        } catch {
          /* ignore */
        }
        navigate(`/audit-trail/${auditTrailBackId}`);
        return;
      }
      navigate(-1);
    } else {
      // Default back navigation
      navigate(-1);
    }
  };

  /**
   * Handle Preview button click
   * Opens preview with cached data (no reload)
   */
  const handlePreviewClick = () => {
    // Close additional information container if open
    if (onCloseAdditionalInfo) {
      onCloseAdditionalInfo();
    }

    const hasCachedNodes =
      Array.isArray(allNodesData) && allNodesData.length > 0;

    if (hasCachedNodes) {
      dispatch(
        openPreviewWithCache({
          partId,
          revisionId,
          revisionData,
          trainingLogs,
          bomTable,
          currentRevision,
          allNodesData,
          fromAuditTrail,
          cancel_screen_data
        }),
      );
    } else {
      dispatch(
        openPreview({
          partId,
          revisionId,
        }),
      );
    }
  };

  /**
   * Check if current user can view this revision
   */
  const canUserView = () => {
    return canUserViewStrict === true;
  };

  /**
   * Handle Submit button click
   * Opens approval modal (internal)
   */
  const handleSubmit = () => {
    handleTaskCheck("submit");
  };

  /**
   * Handle Reject button click
   * Opens rejection modal (internal)
   */
  const handleReject = () => {
    handleTaskCheck("reject");
  };

  /**
   * Handle Approve button click
   * Opens approve modal (internal)
   */
  const handleApprove = () => {
    handleTaskCheck("approve");
  };

  /**
   * Handle Request Change button click
   * Opens request change modal (internal)
   */
  const handleRequestChange = () => {
    handleTaskCheck("request_change");
  };

  /**
   * Handle Release button click
   * Opens release modal (internal)
   */
  const handleRelease = () => {
    handleTaskCheck("release");
  };

  /**
   * Handle Version History click
   * Opens version history popup in parent component
   */
  const handleVersionHistory = () => {
    if (onOpenVersionHistory) {
      onOpenVersionHistory();
    }
  };

  // ============================================================
  // PERMISSION CHECKS
  // ============================================================

  /**
   * Check if current user can submit this revision
   * Returns true if user is in required_by list with purpose "Submit" and is_open = true
   */

  const canUserSubmit = () => {
    let canSubmit = false;
    if (currentRevision) {
      if (
        currentRevision.required_by.some(
          (req) =>
            req.purpose?.toLowerCase() === "submit" &&
            req.user?.id === localStorage.getItem("user_id") &&
            req.is_open === true,
        )
      ) {
        canSubmit = true;
      }
    }
    return canSubmit;
  };

  /**
   * Check if current user can approve this revision
   * Returns true if user is in required_by list with purpose "Approve" and is_open = true
   */
  const canUserApprove = () => {
    const currentUserId = localStorage.getItem("user_id");

    if (!currentUserId || !revisionData || revisionData.length === 0) {
      return false;
    }

    const currentRevision = revisionData.find(
      (rev) => rev.procedure_revision_id === revisionId,
    );

    if (!currentRevision || !currentRevision.required_by) {
      return false;
    }

    return currentRevision.required_by.some(
      (req) =>
        req.purpose?.toLowerCase() === "approve" &&
        req.user?.id === currentUserId &&
        req.is_open === true,
    );
  };

  const canUserReject = () => {
    return permissionCtx.permissions?.Reject === true;
  };

  /**
   * Check if current user has already approved this revision
   */
  const hasUserApproved = () => {
    const currentUserId = localStorage.getItem("user_id");

    if (!currentUserId || !revisionData || revisionData.length === 0) {
      return false;
    }

    const currentRevision = revisionData.find(
      (rev) => rev.procedure_revision_id === revisionId,
    );

    if (!currentRevision || !currentRevision.required_by) {
      return false;
    }

    return currentRevision.required_by.some(
      (req) =>
        req.purpose?.toLowerCase() === "approve" &&
        req.user?.id === currentUserId &&
        req.is_open === false,
    );
  };

  /**
   * Check if current user can release this revision
   * Returns true if user is in required_by list with purpose "Release" and is_open = true
   */
  const canUserRelease = () => {
    const currentUserId = localStorage.getItem("user_id");

    if (!currentUserId || !revisionData || revisionData.length === 0) {
      return false;
    }

    const currentRevision = revisionData.find(
      (rev) => rev.procedure_revision_id === revisionId,
    );

    if (!currentRevision || !currentRevision.required_by) {
      return false;
    }

    return currentRevision.required_by.some(
      (req) =>
        req.purpose?.toLowerCase() === "release" &&
        req.user?.id === currentUserId &&
        req.is_open === true,
    );
  };

  /**
   * Get current revision status
   */
  const getCurrentStatus = () => {
    if (currentStatusProp) {
      return currentStatusProp.toLowerCase();
    }
    const currentRevision = revisionData.find(
      (rev) => rev.procedure_revision_id === revisionId,
    );

    return currentRevision?.status_changes
      ?.find((sc) => sc.is_latest)
      ?.status_changed_to?.toLowerCase();
  };

  // ============================================================
  // BUTTON CONFIGURATION
  // ============================================================

  // Same label as audit preview ProcedureStatusInfo: "type partNumber, Rev …"
  const typePartLabel = [type, partNumber].filter(Boolean).join(" ");
  const title = typePartLabel
    ? `${typePartLabel}, Rev ${revision || ""}`
    : `Rev ${revision || ""}`;

  // Generate subtitle (when from audit trail: saved time from audit API, not "now")
  const savedVersionWhenText = auditTrailSavedAtIso
    ? formatDateToAppTimezoneAlt(auditTrailSavedAtIso, appSetupState)
    : "";
  const subTitleAboveTitle = fromAuditTrail ? (
    <>
      {userName} is Reviewing{" "}
      <span style={{ color: "#0D6EFD" }}>
        Saved Version
        {savedVersionWhenText ? (
          <> on {savedVersionWhenText}.</>
        ) : (
          <>.</>
        )}
      </span>
    </>
  ) : (
    <>{userName} is Viewing</>
  );

  // Get current status
  const currentStatus = getCurrentStatus();
  const hasPendingApprovals = currentRevision?.required_by?.some(
    (req) => req.purpose?.toLowerCase() === "approve" && req.is_open,
  );

  // Default main buttons (vary based on status)
  const defaultButtons = (() => {
    const previewButton = {
      label: "Preview",
      onClick: handlePreviewClick,
      disabled:
        disabled ||
        previewDisabled ||
        !canUserView() ||
        (lockStatus &&
          lockStatus.lock &&
          lockStatus.lock.user_id !== authUser?.id),
      title: 
      // disabled
      //   ? "Another tab is currently editing this document"
      //   : 
        previewDisabled
          ? previewDisabledTitle || "Preview is unavailable right now."
          : !canUserView()
          ? "You do not have permission to view this revision"
          : lockStatus &&
              lockStatus.lock &&
              lockStatus.lock.user_id !== authUser?.id
            ? "Revision is locked by another user"
            : "Preview this revision",
    };

    // If status is "Released", "Archived", or "Rejected", show no buttons
    if (
      currentStatus === "released" ||
      currentStatus === "archived" ||
      currentStatus === "rejected"
    ) {
      return [];
    }

    // If status is "Submitted For Approval", show Request Change and Approve buttons
    if (currentStatus === "submitted for approval") {
      const userApproved = hasUserApproved();
      const userCanApprove = canUserApprove();

      return [
        previewButton,
        {
          label: "Request Change",
          onClick: handleRequestChange,
          disabled: userApproved || !userCanApprove || !canApprove,
          title:
            // disabled
            //   ? "Another tab is currently editing this document"
            //   :
            userApproved
              ? "Cannot request change as you have already approved this revision"
              : !canApprove
                ? "You do not have permission to approve revisions"
                : !userCanApprove
                  ? "You do not have permission to request changes for this revision"
                  : "Request changes to this revision",
        },
        {
          label: "Approve",
          onClick: handleApprove,
          disabled:  userApproved || !userCanApprove || !canApprove,
          title:
            // disabled
            //   ? "Another tab is currently editing this document"
            //   :
            userApproved
              ? "You have already approved this revision"
              : !canApprove
                ? "You do not have permission to approve revisions"
                : !userCanApprove
                  ? "You are not an approver for this revision"
                  : "Approve this revision",
        },
      ];
    }

    // If status is "Approved", show Release button only
    if (currentStatus === "approved" && !hasPendingApprovals) {
      const userCanRelease = canUserRelease();

      return [
        previewButton,
        {
          label: "Release",
          onClick: handleRelease,
          disabled: !userCanRelease || !canRelease,
          title:
            // disabled
            //   ? "Another tab is currently editing this document"
            //   :
            !canRelease
              ? "You do not have permission to release revisions"
              : !userCanRelease
                ? "You don't have permission to release this revision"
                : "Release this revision",
        },
      ];
    }

    // Default: Show Submit button for other statuses
    return [
      previewButton,
      {
        label: "Submit",
        onClick: handleSubmit,
        disabled:
          disabled ||
          !canUserSubmit() ||
          (lockStatus &&
            lockStatus.lock &&
            lockStatus.lock.user_id !== authUser?.id),
        title:
          // disabled
          //   ? "Another tab is currently editing this document"
          //   :
          !canUserSubmit()
            ? "You are not authorized to submit this revision"
            : lockStatus &&
                lockStatus.lock &&
                lockStatus.lock.user_id !== authUser?.id
              ? "Revision is locked by another user"
              : "Submit this revision for approval",
      },
    ];
  })();

  const getTrainingButton = {
    label: "Get Training",
    onClick: () => {
      if (onGetTrainingClick) {
        onGetTrainingClick();
        return;
      }
      showSnackbar("Get Training is coming soon.", "info");
    },
    disabled: false,
    title: "Get training for this document",
  };

  const giveTrainingButton = {
    label: "Give Training",
    onClick: () => {
      if (onGiveTrainingClick) {
        onGiveTrainingClick();
        return;
      }
      if (onGetTrainingClick) {
        onGetTrainingClick();
        return;
      }
      showSnackbar("Give Training is coming soon.", "info");
    },
    disabled: false,
    title: "Give training for this document",
  };

  /**
   * Handle Archive button click
   * Archives the current revision
   */
  const performArchive = async () => {
    // Close additional information container if open
    if (onCloseAdditionalInfo) {
      onCloseAdditionalInfo();
    }
    try {
      const revData = revisionData.find(
        (r) => r.procedure_revision_id === revisionId,
      );

      if (!revData) {
        showSnackbar("Revision data not found.", "error");
        return;
      }

      const islatest =
        revData.status_changes?.find((s) => s.is_latest === true) || {};

      const ad = await getAuditTrailData({
        action: "REVISION_ARCHIVED",
        comment: "Manually Archived",
        parent_version: revData.version_id_ref,
        version_content: {
          ...islatest,
          part_ref: partId,
          rev: revision,
          procedure_revision_id: revisionId,
          current_status: "Archived",
          action_to_show: `Archived ${partNumber} Rev ${revision}`,
          procedure_number: partNumber,
          type: type,
          description: description,
          item_code_type: type,
          partNumber: partNumber,
          status: "Archived",
          status_changed_to: "Archived",
          revisionNodes: allNodesData || [],
        },
      });

      const payload = {
        procedure_revision_id_ref: revisionId,
        status_changed_to: "Archived",
        part_id_ref: partId,
        is_in_release_history: true,
        past_procedure_revisions_reviewed: { ...islatest },
        audit_trail_data: ad,
        tree_node_id: revData?.tree_node_details?.tree_node_id,
      };

      // return;
      const response = await axios.post(
        `${BASE_URL}procedure-revision-status/archived-manual`,
        payload,
      );

      if (response.data.success) {
        showSnackbar("Revision archived successfully.", "success");
        setTimeout(() => {
          navigate(
            `/archived-revision/${partId}/${revision}/${type}/${partNumber}/no-description/Archived/${revisionId}`,
            { state: { status: "Archived" } },
          );
          dispatch(getProcedureRevisionsForPart({ partId }));
        }, 1000);
      } else {
        showSnackbar(
          response.data.message,
          "error",
        );
      }
    } catch (error) {
      showSnackbar(
        `Error archiving revision: ${error.message || "Unknown error"}`,
        "error",
      );
    }
  };

  const handleArchive = () => {
    handleTaskCheck("archive");
  };

  // Default buttons before title
  const defaultButtonsBeforeTitle = (() => {
    if (!revisionData.length) return [];

    // Released: Archive only in edit mode; live/view mode hides workflow actions here.
    if (currentStatus === "archived") return [];
    if (currentStatus === "released") {
      if (isViewMode) return [];
      return [
        {
          label: "Archive",
          onClick: handleArchive,
          disabled: disabled || !canArchive,
          title: !canArchive
            ? "You do not have permission to archive revisions"
            : "Archive this revision",
        },
      ];
    }

    if (isViewMode) return [];

    // Count non-rejected revisions (excluding "MAINTAINED OUTSIDE OF SYSTEM")
    const nonRejectedRevisions = revisionData.filter((rev) => {
      const status = rev?.status_changes
        ?.find((sc) => sc.is_latest)
        ?.status_changed_to?.toLowerCase();
      return status !== "rejected" && status !== "maintained outside of system";
    });

    // Show reject button only if there's more than one revision AND more than one non-rejected revision
    return revisionData.length > 1 &&
      nonRejectedRevisions.length > 1 &&
      currentStatus !== "rejected"
      ? [
          {
            label: "Reject",
            onClick: handleReject,
            disabled:
              disabled ||
              !canUserReject() ||
              !canReject ||
              (lockStatus &&
                lockStatus.lock &&
                lockStatus.lock.user_id !== authUser?.id),
            title:
              //disabled
              // ? "Another tab is currently editing this document"
              // :

              !canReject
                ? "You do not have permission to reject revisions"
                : !canUserReject()
                  ? "You do not have permission to reject this revision"
                  : lockStatus &&
                      lockStatus.lock &&
                      lockStatus.lock.user_id !== authUser?.id
                    ? "Revision is locked by another user"
                    : "Reject this revision",
          },
        ]
      : [];
  })();

  // Get submissionBy for reject modal
  const getSubmissionBy = () => {
    const currentRevision = revisionData.find(
      (rev) => rev.procedure_revision_id === revisionId,
    );
    const submitter = currentRevision?.required_by?.find(
      (req) => req.purpose?.toLowerCase() === "submit",
    );
    return submitter?.user_ref || "";
  };

  // Prepare preview data for Eye Icon (for Released and Archived statuses)
  const previewData =
    currentStatus === "released" || currentStatus === "archived"
      ? {
          status: currentStatus,
          partNumber,
          type,
          description,
          revision,
          partId,
          revisionId,
          element: {
            type: "released-revision",
            partId,
            revision,
            partType: type,
            description,
            status: currentStatus,
            created_at: new Date().toISOString(),
          },
        }
      : null;

  useEffect(() => {
    if (currentRevision) {
      setTaskReference(
        dispatch,
        `Rev ${currentRevision.rev} , ${partNumber}`,
        currentRevision.procedure_revision_id,
        currentRevision?.tree_node_details?.tree_node_id,
        currentRevision?.tree_node_details.ltree_path,
        true
      );
    }
  }, [currentRevision]);

  useEffect(() => {
    if (!isViewMode || !revisionId) {
      setViewerTrainingStatusLabel(null);
      return;
    }
    let cancelled = false;
    setViewerTrainingStatusLabel(null);
    (async () => {
      const { status } = await fetchTrainingStatusLabelForProcedureRevisionForTrainee(
        dispatch,
        revisionId,
        authUser?.id,
        partId,
      );
      if (!cancelled) setViewerTrainingStatusLabel(status);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isViewMode,
    revisionId,
    dispatch,
    trainingLogs?.length,
    trainingStatusRefreshKey,
    partId,
    authUser?.id,
  ]);

  // When from audit trail: only Preview button (always enabled so it works like normal)
  const auditTrailPreviewButton = {
    label: "Preview",
    onClick: handlePreviewClick,
    disabled: false,
    title: "Preview this revision",
  };
  /** Prior same-letter revision (e.g. A-) fully trained → show Trained on A1 when current rev has no log yet. */
  const inheritedTrainedTraineeIdsForCurrentRevision = useMemo(() => {
    const letterContextRev = resolveRevisionRevLabelFromContext(
      revisionData,
      revisionId,
      revision,
    );
    const eff = findLatestReleasedProcedureRevision(
      revisionData,
      letterContextRev,
    );
    return buildInheritedFullyTrainedTraineeIds({
      trainingLogs,
      revisionData,
      currentProcedureRevisionId: eff?.procedureRevisionId ?? revisionId,
      currentRev: eff?.rev ?? revision,
    });
  }, [trainingLogs, revisionData, revisionId, revision]);

  /**
   * Table of Procedures (Add Module live) passes `viewerTrainingStatus` in `location.state` and in
   * `sessionStorage` key `navFromAddModule_${partId}_${revisionId}` so the AppBar matches the table
   * when state is lost on refresh or deploy.
   */
  const addModuleTableTrainingHint = useMemo(() => {
    const fromState = location.state?.fromAddModule === true;
    const vsState = location.state?.viewerTrainingStatus;
    if (
      fromState &&
      vsState != null &&
      String(vsState).trim() !== ""
    ) {
      return {
        fromAddModule: true,
        viewerTrainingStatus: String(vsState).trim(),
      };
    }
    if (!partId || !revisionId) {
      return { fromAddModule: false, viewerTrainingStatus: null };
    }
    try {
      const storageKey = `navFromAddModule_${partId}_${revisionId}`;
      const stored = sessionStorage.getItem(storageKey);
      if (!stored) {
        return { fromAddModule: false, viewerTrainingStatus: null };
      }
      const parsed = JSON.parse(stored);
      if (parsed?.fromAddModule !== true) {
        return { fromAddModule: false, viewerTrainingStatus: null };
      }
      const vs = parsed?.viewerTrainingStatus;
      if (vs != null && String(vs).trim() !== "") {
        return {
          fromAddModule: true,
          viewerTrainingStatus: String(vs).trim(),
        };
      }
    } catch {
      /* ignore */
    }
    return { fromAddModule: false, viewerTrainingStatus: null };
  }, [
    location.state?.fromAddModule,
    location.state?.viewerTrainingStatus,
    partId,
    revisionId,
  ]);

  /** Trained → Give Training only; any other status (or loading) → Get Training only */
  const effectiveViewerTrainingStatusLabel = useMemo(() => {
    // AddModule live-mode table resolves training status from procedure-revision/get-all and passes it
    // via navigation state as `viewerTrainingStatus`. That value can become stale after completing a
    // training session and redirecting back here; in that case Document bumps
    // `trainingStatusRefreshKey` to force a refetch, so prefer the freshly fetched value.
    if (
      trainingStatusRefreshKey === 0 &&
      addModuleTableTrainingHint.fromAddModule &&
      addModuleTableTrainingHint.viewerTrainingStatus
    ) {
      return addModuleTableTrainingHint.viewerTrainingStatus;
    }
    if (isRevisionWorkflowActor(currentRevision, authUser?.id)) return "Trained";
    const base = viewerTrainingStatusLabel;
    const tid =
      authUser?.id != null ? String(authUser.id).trim().toLowerCase() : "";
    if (
      tid &&
      inheritedTrainedTraineeIdsForCurrentRevision.has(tid) &&
      base !== null &&
      base !== "Partially Trained" &&
      !/\bStep\b/i.test(String(base || "")) &&
      base === "Not Trained"
    ) {
      return "Trained";
    }
    return base;
  }, [
    addModuleTableTrainingHint,
    trainingStatusRefreshKey,
    currentRevision,
    authUser?.id,
    viewerTrainingStatusLabel,
    inheritedTrainedTraineeIdsForCurrentRevision,
  ]);

  const viewModeTrainingButtons =
    effectiveViewerTrainingStatusLabel === "Trained"
      ? [giveTrainingButton]
      : [getTrainingButton];

  /** Live/view mode: workflow actions (Preview/Submit/…) are edit-only; show training only. */
  const effectiveButtons = fromAuditTrail
    ? [auditTrailPreviewButton]
    : customButtons ||
      (isViewMode ? viewModeTrainingButtons : defaultButtons);
  const effectiveButtonsBeforeTitle = fromAuditTrail
    ? []
    : customButtonsBeforeTitle || defaultButtonsBeforeTitle;

  // GET/training status label can be returned as "Step 1", "Step 1 & 2", etc.
  // Header should show a simplified status: any "Step ..." label => "Partially Trained".
  const viewerTrainingStatusLabelForDisplay =
    effectiveViewerTrainingStatusLabel === null
      ? null
      : typeof effectiveViewerTrainingStatusLabel === "string" &&
          /\bStep\b/i.test(effectiveViewerTrainingStatusLabel)
        ? "Partially Trained"
        : effectiveViewerTrainingStatusLabel;

  const trainingStatusSubheading = isViewMode ? (
    <>
      Training Status:{" "}
      <span
        style={{
          color:
            viewerTrainingStatusLabelForDisplay === null
              ? "#6c757d"
              : viewerTrainingStatusLabelForDisplay === "Trained"
                ? "#198754"
                : viewerTrainingStatusLabelForDisplay === "Not Trained"
                  ? "#dc3545"
                  : "#fd7e14",
        }}
      >
        {viewerTrainingStatusLabelForDisplay === null
          ? "Loading…"
          : viewerTrainingStatusLabelForDisplay}
      </span>
    </>
  ) : (
    ""
  );

  const handleTrainingHelpClick = () => {
    if (onCloseAdditionalInfo) {
      onCloseAdditionalInfo();
    }
    if (onTrainingHelpClick) {
      onTrainingHelpClick();
      return;
    }
    // showSnackbar("Training help is coming soon.", "info");
  };

  const truncateWithEllipsis = (value, maxChars = 28) => {
    const str = value == null ? "" : String(value);
    if (!str) return "";
    if (str.length <= maxChars) return str;
    const sliceTo = Math.max(0, maxChars - 3);
    return str.slice(0, sliceTo).trimEnd() + "...";
  };

  return (
    <>
      {trainingCheckmarkMode ? (
        <header className="appbar document-training-checkmark-appbar">
          <div className="document-training-checkmark-appbar__left">
            <OutlineButton
              variant="outline-gray"
              size="md"
              type="button"
              onClick={onTrainingCheckmarkCancel}
            >
              Cancel
            </OutlineButton>
            <span className="document-training-checkmark-appbar__session">
              <span title={trainerDisplayName || "—"}>
                {truncateWithEllipsis(trainerDisplayName || "—")}
              </span>{" "}
              is Training{" "}
              <span title={traineeDisplayName || "—"}>
                {truncateWithEllipsis(traineeDisplayName || "—")}
              </span>
            </span>
          </div>
          <div className="document-training-checkmark-appbar__center">
            <h2 className="document-training-checkmark-appbar__title">
              Checkmark Where Trained
            </h2>
          </div>
          <div className="document-training-checkmark-appbar__right">
            <PrimaryButton type="button" onClick={onTrainingCheckmarkNext}>
              Next
            </PrimaryButton>
          </div>
        </header>
      ) : (
        <AppBar
          title={title}
          showTablesDropdown={showTablesDropdown}
          showBlackTitle={showBlackTitle}
          buttons={!usingInMagicLink ? effectiveButtons : []}
          buttonsBeforeTitle={
            effectiveButtonsBeforeTitle
          }
          showBookmark={showBookmark}
          onBookmarkClick={onBookmarkClick}
          bookmarkVariant={isViewMode ? "edit" : "bookmark"}
          showProfileButton={showProfileButton}
          subheading={trainingStatusSubheading}
          navigateTwoPagesBack={navigateTwoPagesBack}
          subTitleAboveTitle={!usingInMagicLink && subTitleAboveTitle}
          versionHistoryHandler={handleVersionHistory}
          showEyeIcon={
            !isViewMode &&
            (currentStatus === "released" ||
              currentStatus === "archived" ||
              currentStatus === "rejected")
          }
          previewData={previewData}
          onPreviewClick={handlePreviewClick}
          previewIconDisabled={previewDisabled}
          previewIconTitle={
            previewDisabled
              ? previewDisabledTitle ||
                "Preview will be enabled once all nodes are loaded."
              : "Preview this revision"
          }
          onBack={onBack || handleBackClick}
          onTablesDropdownOpen={onCloseAdditionalInfo}
          showBackButton={showBackButton}
          showQuestionIcon={
            isViewMode && !fromAuditTrail && !usingInMagicLink
          }
          onQuestionIconClick={handleTrainingHelpClick}
        />
      )}

      {/* Internal Modals */}
      <SubmitRevisionModal
        isOpen={isApprovalModalOpen}
        onClose={() => setIsApprovalModalOpen(false)}
        partId={partId}
        partNumber={partNumber}
        type={type}
        description={description}
        myRevision={revision}
        revisionId={revisionId}
        revisionData={revisionData}
        revisionNodes={allNodesData}
        onSuccess={onSubmitSuccess}
      />

      <RejectRevisionModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        partId={partId}
        partNumber={partNumber}
        type={type}
        description={description}
        myRevision={revision}
        revisionId={revisionId}
        revisionData={revisionData}
        submissionBy={getSubmissionBy()}
        onSuccess={onRejectSuccess}
      />

      <ApproveRevisionModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        partId={partId}
        partNumber={partNumber}
        type={type}
        description={description}
        myRevision={revision}
        revisionId={revisionId}
        revisionData={revisionData}
        onSuccess={(result) => {
          setIsApproveModalOpen(false);
          if (result?.message) {
            showSnackbar(result.message, "success");
          }
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        }}
      />

      <RequestChangeModal
        isOpen={isRequestChangeModalOpen}
        onClose={() => setIsRequestChangeModalOpen(false)}
        partId={partId}
        partNumber={partNumber}
        type={type}
        description={description}
        myRevision={revision}
        revisionId={revisionId}
        revisionData={revisionData}
        revisionNodes={allNodesData}
        onSuccess={() => {
          setIsRequestChangeModalOpen(false);
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        }}
      />

      <ReleaseRevisionModal
        isOpen={isReleaseModalOpen}
        onClose={() => setIsReleaseModalOpen(false)}
        partId={partId}
        partNumber={partNumber}
        type={type}
        description={description}
        myRevision={revision}
        revisionId={revisionId}
        revisionData={revisionData}
        onSuccess={() => {
          setIsReleaseModalOpen(false);
          if (onSubmitSuccess) {
            onSubmitSuccess();
          }
        }}
      />

      {/* Task Check Popup */}
      {openTasksState.isOpen && (
        <OpenTasksPopup
          closeModal={() =>
            setOpenTasksState({
              isOpen: false,
              ltree_path: null,
              operation: null,
              title: null,
            })
          }
          procedure_ltree_ref={openTasksState.ltree_path}
          title={openTasksState.title}
          isContinueDisabled={openTasksState.operation !== "request_change" && openTasksState.operation !== "reject"}
          onContinue={() => {
            proceedWithOperation(openTasksState.operation);
          }}
        />
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default DocumentAppBar;
